import { Component, Input, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { RouterLink } from '@angular/router';

/**
 * Reusable Password Input Component
 * Features show/hide toggle and optional password requirements checklist
 * Implements ControlValueAccessor for reactive forms integration
 */
@Component({
  selector: 'app-form-password',
  standalone: true,
  imports: [CommonModule, MaxLengthDirective, RouterLink],
  templateUrl: './form-password.component.html',
  styleUrls: ['./form-password.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormPasswordComponent),
      multi: true
    }
  ]
})
export class FormPasswordComponent implements ControlValueAccessor {
  @Input() label: string = 'Password';
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() errorMessage: string = '';
  @Input() showRequirements: boolean = false;
  @Input() showForgotPassword: boolean = false;

  value: string = '';
  showPassword: boolean = false;
  onChange: any = () => {};
  onTouched: any = () => {};

  /**
   * Password requirements validation
   */
  get requirements() {
    return {
      minLength: this.value.length >= 8,
      hasUpperCase: /[A-Z]/.test(this.value),
      hasLowerCase: /[a-z]/.test(this.value),
      hasNumber: /[0-9]/.test(this.value),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(this.value)
    };
  }

  get allRequirementsMet(): boolean {
    return Object.values(this.requirements).every(req => req);
  }

  /**
   * Write value from form control to component
   */
  writeValue(value: string): void {
    this.value = value || '';
  }

  /**
   * Register onChange callback
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * Register onTouched callback
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Handle disabled state
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Handle input change
   */
  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
    this.onChange(this.value);
  }

  /**
   * Handle input blur
   */
  onInputBlur(): void {
    this.onTouched();
  }

  /**
   * Toggle password visibility
   */
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
}
